import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdCheckCircle,
  MdErrorOutline,
  MdSecurity,
  MdLock,
} from "react-icons/md";
import {
  TbCurrencyRupee,
  TbLoader2,
  TbShieldCheck,
  TbAlertTriangle,
  TbWallet,
  TbPlus,
  TbArrowRight,
} from "react-icons/tb";
import { FiCheck, FiCopy, FiExternalLink } from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../../Utils/axiosInstance";
import RechargeModal from "../Recharge/RechargeModal";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const BuyerPenaltyPaymentModal = ({
  isOpen,
  onClose,
  selectedPenalties = [],
  user,
  wallet,
  fetchWallet,
  onSuccess,
}) => {
  const [animate, setAnimate] = useState(false);
  const [step, setStep] = useState("review"); // "review" | "processing" | "success" | "failed"
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastPaymentData, setLastPaymentData] = useState(null);
  const [showRechargeModal, setShowRechargeModal] = useState(false);

  useLockBodyScroll(isOpen);

  const totalAmount = selectedPenalties.reduce(
    (sum, p) => sum + (p.amounts?.totalAmount || 0),
    0
  );

  const availableBalance = Number(wallet?.availableBalance || 0);
  const hasSufficientBalance = availableBalance >= totalAmount;
  const shortfall = Math.max(0, totalAmount - availableBalance);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      setStep("review");
      setLoading(false);
      setErrorMessage("");
      setLastPaymentData(null);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (step === "processing") return;
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  if (!isOpen) return null;

  // 1. Pay via Wallet
  const handlePayViaWallet = async () => {
    if (!selectedPenalties.length || totalAmount <= 0) {
      toast.error("Please select at least one valid penalty to pay");
      return;
    }

    if (!hasSufficientBalance) {
      setShowRechargeModal(true);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setStep("processing");

    try {
      const penaltyIds = selectedPenalties.map((p) => p._id);
      const res = await axios.post("/penalties/pay-via-wallet", {
        penaltyIds,
      });

      if (res.data?.success) {
        setLastPaymentData({
          method: "Wallet",
          transactionId: res.data.transaction?._id || "TXN-WALLET",
          amount: totalAmount,
          penaltyCount: selectedPenalties.length,
          paidPenalties: res.data.data || selectedPenalties,
          unblocked: res.data.unblocked,
        });
        setStep("success");
        toast.success("Penalties settled successfully via Wallet!");
        if (typeof fetchWallet === "function") {
          await fetchWallet();
        }
        if (typeof onSuccess === "function") {
          onSuccess();
        }
      } else {
        setStep("failed");
        setErrorMessage(res.data?.message || "Wallet payment failed");
      }
    } catch (err) {
      console.error("Wallet penalty settlement failed:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Payment failed. Please check your wallet balance and try again.";
      setErrorMessage(msg);
      setStep("failed");
    } finally {
      setLoading(false);
    }
  };

  // 2. Pay via Razorpay Gateway (direct alternative)
  const handlePayViaRazorpay = async () => {
    if (!selectedPenalties.length || totalAmount <= 0) {
      toast.error("Please select at least one valid penalty to pay");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setLoading(false);
        setErrorMessage(
          "Could not load Razorpay payment gateway. Please check your internet connection."
        );
        setStep("failed");
        return;
      }

      let orderData;
      try {
        const orderRes = await axios.post("/recharge/initiate-recharge", {
          amount: totalAmount,
        });

        if (!orderRes.data?.success) {
          setLoading(false);
          setErrorMessage(
            orderRes.data?.message || "Failed to create payment order."
          );
          setStep("failed");
          return;
        }
        orderData = orderRes.data.data;
      } catch (err) {
        setLoading(false);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to initiate payment. Please try again.";
        setErrorMessage(msg);
        setStep("failed");
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_5wDk8c9pG7v1u2",
        amount: orderData.amount,
        currency: orderData.currency || "INR",
        name: "PCM Exports",
        description: `Settlement for ${selectedPenalties.length} Buyer Penalty/ies`,
        order_id: orderData.orderId,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: {
          color: "#772037",
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast("Payment cancelled", { icon: "ℹ️" });
          },
        },
        handler: async (response) => {
          setStep("processing");
          try {
            const verifyRes = await axios.post("/penalties/pay-via-razorpay", {
              penaltyIds: selectedPenalties.map((p) => p._id),
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyRes.data?.success) {
              setLastPaymentData({
                method: "Razorpay",
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: totalAmount,
                penaltyCount: selectedPenalties.length,
                paidPenalties: verifyRes.data.data || selectedPenalties,
                unblocked: verifyRes.data.unblocked,
              });
              setStep("success");
              toast.success("Penalties settled successfully!");
              if (typeof fetchWallet === "function") {
                fetchWallet();
              }
              if (typeof onSuccess === "function") {
                onSuccess();
              }
            } else {
              setStep("failed");
              setErrorMessage(
                verifyRes.data?.message ||
                  "Verification failed. If money was deducted, please contact support."
              );
            }
          } catch (err) {
            console.error("Penalty verification error:", err);
            setStep("failed");
            setErrorMessage(
              err.response?.data?.message ||
                "Payment verification error. Please contact support with payment ID: " +
                  response.razorpay_payment_id
            );
          } finally {
            setLoading(false);
          }
        },
      };

      const rzpInstance = new window.Razorpay(options);
      rzpInstance.on("payment.failed", (failResponse) => {
        console.error("Payment failed:", failResponse.error);
        setStep("failed");
        setErrorMessage(
          failResponse.error?.description ||
            "Payment failed. Please try again or use another payment method."
        );
        setLoading(false);
      });

      rzpInstance.open();
    } catch (err) {
      console.error("Unexpected error in Razorpay checkout:", err);
      setLoading(false);
      setStep("failed");
      setErrorMessage("Something went wrong. Please try again.");
    }
  };

  const copyText = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 font-Urbanist ${
          animate ? "opacity-100" : "opacity-0"
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget && step !== "processing") {
            handleClose();
          }
        }}
      >
        <div
          className={`bg-white dark:bg-gray-900 w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${
            animate
              ? "scale-100 translate-y-0"
              : "scale-95 md:translate-y-4 translate-y-full"
          }`}
          style={{ maxHeight: "90vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-5 py-4 bg-primaryColor text-white flex items-center justify-between flex-shrink-0">
            <button
              type="button"
              disabled={step === "processing"}
              onClick={handleClose}
              className="text-2xl leading-none text-white/90 hover:text-white transition disabled:opacity-30 cursor-pointer"
            >
              ←
            </button>
            <div className="flex-1 text-center font-semibold text-lg text-white">
              {step === "success"
                ? "Settlement Successful"
                : step === "failed"
                ? "Payment Failed"
                : step === "processing"
                ? "Processing Payment"
                : "Settle Buyer Penalties"}
            </div>
            {step !== "processing" ? (
              <button
                type="button"
                onClick={handleClose}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition cursor-pointer"
              >
                <MdClose size={18} />
              </button>
            ) : (
              <div className="w-8" />
            )}
          </div>

          <div className="overflow-y-auto flex-1">
          {/* ══════════════════════════════════════════════════════════════════
              STEP: REVIEW
          ══════════════════════════════════════════════════════════════════ */}
          {step === "review" && (
            <div className="p-6 space-y-5">
              {/* Selected Penalties Breakdown */}
              <div className="rounded-2xl bg-gray-50 dark:bg-gray-800/50 p-4 border border-gray-100 dark:border-gray-800 space-y-3">
                <div className="flex items-center justify-between text-xs text-gray-500 pb-2 border-b border-gray-200/60 dark:border-gray-700/60 font-medium">
                  <span>Selected Penalty ({selectedPenalties.length})</span>
                  <span>Amount</span>
                </div>

                <div className="max-h-36 overflow-y-auto space-y-2 pr-1 no-scrollbar text-xs">
                  {selectedPenalties.map((p) => (
                    <div
                      key={p._id}
                      className="flex items-center justify-between text-gray-700 dark:text-gray-300"
                    >
                      <div className="flex flex-col min-w-0 pr-2">
                        <span className="font-semibold truncate text-gray-900 dark:text-gray-100">
                          {p.uniquePenaltyId || p._id.slice(-8).toUpperCase()}
                        </span>
                        <span className="text-[11px] text-gray-400 truncate">
                          {p.reason?.title || p.penaltyType || "Penalty Due"}
                        </span>
                      </div>
                      <span className="font-bold text-gray-900 dark:text-white whitespace-nowrap">
                        ₹{(p.amounts?.totalAmount || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Total Row */}
                <div className="pt-2.5 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide">
                    Total Settle Amount
                  </span>
                  <div className="text-right">
                    <span className="text-xl font-black text-primaryColor">
                      ₹{totalAmount.toLocaleString("en-IN")}
                    </span>
                    <span className="block text-[10px] text-gray-400">
                      Inclusive of applicable GST
                    </span>
                  </div>
                </div>
              </div>

              {/* WALLET BALANCE & RECHARGE CARD */}
              <div className="rounded-2xl p-4 border border-primaryColor/20 bg-gradient-to-br from-primaryColor/5 via-primaryColor/10 to-transparent dark:from-primaryColor/10 dark:via-primaryColor/5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-primaryColor/15 flex items-center justify-center text-primaryColor">
                      <TbWallet size={18} />
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500 font-semibold tracking-wide uppercase">
                        Wallet Balance
                      </p>
                      <p className="text-base font-extrabold text-gray-900 dark:text-white leading-tight">
                        ₹{availableBalance.toLocaleString("en-IN")}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowRechargeModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition shadow-xs"
                  >
                    <TbPlus size={14} />
                    <span>Add Money</span>
                  </button>
                </div>

                {/* Balance Status Message */}
                {hasSufficientBalance ? (
                  <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl px-3 py-2 text-xs">
                    <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-medium">
                      <FiCheck size={14} className="text-emerald-600" />
                      <span>Sufficient balance available in wallet</span>
                    </div>
                    <span className="text-[11px] text-emerald-600 font-semibold">
                      ₹{(availableBalance - totalAmount).toLocaleString("en-IN")}{" "}
                      left after payment
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2 text-xs">
                    <div className="flex items-center gap-1.5 text-amber-800 dark:text-amber-400 font-semibold">
                      <TbAlertTriangle size={15} className="text-amber-600 shrink-0" />
                      <span>Low wallet balance (Shortfall: ₹{shortfall.toLocaleString("en-IN")})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowRechargeModal(true)}
                      className="text-primaryColor font-bold underline hover:text-primaryColor/80 text-left sm:text-right"
                    >
                      Recharge ₹{shortfall.toLocaleString("en-IN")} now
                    </button>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2.5 pt-1">
                {/* Primary Button: Pay via Wallet */}
                <button
                  type="button"
                  onClick={handlePayViaWallet}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-primaryColor hover:bg-primaryColor/90 text-white font-bold text-sm transition-all shadow-md active:scale-98 disabled:opacity-50"
                >
                  <TbWallet size={18} />
                  <span>
                    {hasSufficientBalance
                      ? `Pay ₹${totalAmount.toLocaleString("en-IN")} from Wallet`
                      : `Recharge & Pay ₹${totalAmount.toLocaleString("en-IN")}`}
                  </span>
                </button>

                {/* Secondary Option: Direct Gateway Payment */}
                <button
                  type="button"
                  onClick={handlePayViaRazorpay}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-xs transition"
                >
                  <span>Or Pay Directly via Online Gateway (UPI/Card)</span>
                  <TbArrowRight size={14} />
                </button>
              </div>

              {/* Trust Badge */}
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-400 text-center">
                <MdSecurity className="text-emerald-500" size={14} />
                <span>Instant unblocking upon successful settlement</span>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              STEP: PROCESSING
          ══════════════════════════════════════════════════════════════════ */}
          {step === "processing" && (
            <div className="p-8 flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primaryColor/10 flex items-center justify-center text-primaryColor">
                <TbLoader2 size={32} className="animate-spin" />
              </div>
              <div>
                <h4 className="text-base font-bold text-gray-900 dark:text-white">
                  Settling Penalties...
                </h4>
                <p className="text-xs text-gray-400 mt-1 max-w-xs">
                  Please do not close or refresh this tab while we verify and update your account records.
                </p>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              STEP: SUCCESS
          ══════════════════════════════════════════════════════════════════ */}
          {step === "success" && (
            <div className="p-6 space-y-5">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 flex items-center justify-center">
                  <MdCheckCircle size={36} />
                </div>
                <h4 className="text-lg font-black text-gray-900 dark:text-white">
                  Settlement Completed!
                </h4>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  ₹{lastPaymentData?.amount?.toLocaleString("en-IN")} was paid
                  via {lastPaymentData?.method || "Wallet"}. {lastPaymentData?.penaltyCount} penalty obligation(s) marked as settled.
                </p>
              </div>

              {/* Unblocked Badge */}
              <div className="p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-center space-y-1">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 dark:text-emerald-300">
                  <TbShieldCheck size={16} />
                  Account In Good Standing
                </span>
                <p className="text-[11px] text-emerald-700 dark:text-emerald-400">
                  Your order posting privileges and buyer access are active. You can now post new requirements anytime.
                </p>
              </div>

              {/* Receipt details */}
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 p-3.5 border border-gray-100 dark:border-gray-700/60 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Payment Mode:</span>
                  <span className="font-bold text-gray-700 dark:text-gray-300">
                    {lastPaymentData?.method || "Wallet"}
                  </span>
                </div>
                {lastPaymentData?.transactionId && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Transaction ID:</span>
                    <button
                      type="button"
                      onClick={() =>
                        copyText(lastPaymentData.transactionId, "Transaction ID")
                      }
                      className="font-mono font-semibold text-primaryColor flex items-center gap-1 hover:underline"
                    >
                      <span>
                        {lastPaymentData.transactionId.length > 20
                          ? `${lastPaymentData.transactionId.slice(0, 18)}...`
                          : lastPaymentData.transactionId}
                      </span>
                      <FiCopy size={12} />
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Amount Paid:</span>
                  <span className="font-bold text-emerald-600">
                    ₹{lastPaymentData?.amount?.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="w-full py-3 px-4 rounded-xl bg-primaryColor text-white font-bold text-xs hover:bg-primaryColor/90 transition shadow-sm cursor-pointer"
              >
                Done
              </button>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              STEP: FAILED
          ══════════════════════════════════════════════════════════════════ */}
          {step === "failed" && (
            <div className="p-6 space-y-4">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 mx-auto rounded-full bg-red-100 dark:bg-red-950/60 text-red-600 flex items-center justify-center">
                  <MdErrorOutline size={32} />
                </div>
                <h4 className="text-base font-bold text-gray-900 dark:text-white">
                  Payment Unsuccessful
                </h4>
                <p className="text-xs text-red-600 dark:text-red-400 max-w-sm mx-auto">
                  {errorMessage ||
                    "We could not complete your payment. Please try again."}
                </p>
              </div>

              <div className="pt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStep("review")}
                  className="flex-1 py-3 px-4 rounded-xl bg-primaryColor text-white font-bold text-xs hover:bg-primaryColor/90 transition shadow-xs cursor-pointer"
                >
                  Try Again
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-3 px-4 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold text-xs hover:bg-gray-200 transition cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* RECHARGE WALLET MODAL */}
      {showRechargeModal && (
        <RechargeModal
          isOpen={showRechargeModal}
          onClose={() => setShowRechargeModal(false)}
          wallet={wallet}
          user={user}
          onSuccess={async () => {
            if (typeof fetchWallet === "function") {
              await fetchWallet();
            }
            setShowRechargeModal(false);
          }}
        />
      )}
    </>,
    document.body
  );
};

export default BuyerPenaltyPaymentModal;
